import { FluentProvider, mergeClasses } from '@fluentui/react-components';
import type { ReactNode } from 'react';
import { bannerTheme, sidebarTheme } from '#src/theme/identity';
import { useThemePreference } from '#src/theme/ThemeProvider';

const THEMES = { sidebar: sidebarTheme, banner: bannerTheme } as const;

/**
 * Área em azul-marinho da identidade (navegação, banners). Os componentes
 * dentro dela usam os tokens de sempre, resolvidos pelo tema azul: texto,
 * hover, foco e o botão principal (dourado) já vêm legíveis. No alto
 * contraste vale o tema do sistema: nada de cores próprias.
 */
export function IdentitySurface({
  kind,
  className,
  identityClassName,
  children,
}: {
  kind: keyof typeof THEMES;
  /** Vale sempre (layout). */
  className?: string;
  /** Só com a identidade ativa (fundos, decoração). */
  identityClassName?: string;
  children: ReactNode;
}) {
  const { mode } = useThemePreference();
  if (mode === 'contrast') {
    return <div className={className}>{children}</div>;
  }
  // Sem `applyStylesToPortals`: o container dos popups (tooltips, menus)
  // recebe só as variáveis do tema. Com ele, recebia também as classes da
  // raiz (altura, fundo azul) e cobria a tela sobre o próprio gatilho.
  return (
    <FluentProvider
      theme={THEMES[kind]}
      applyStylesToPortals={false}
      className={mergeClasses(className, identityClassName)}
    >
      {children}
    </FluentProvider>
  );
}

/** A fileira de estrelas douradas do modelo de etiqueta, como ornamento. */
export function GoldStars({ className }: { className?: string }) {
  const sizes = [10, 14, 18, 14, 10];
  return (
    <svg
      className={className}
      viewBox="0 0 112 24"
      width="112"
      height="24"
      aria-hidden
      focusable="false"
    >
      {sizes.map((size, index) => {
        const center = 12 + index * 22;
        const half = size / 2;
        const curve = half * 0.18;
        return (
          <path
            key={center}
            fill="currentColor"
            opacity={0.55 + (size / 18) * 0.45}
            d={`M${center} ${12 - half} C${center + curve} ${12 - curve} ${center + curve} ${12 - curve} ${center + half} 12 C${center + curve} ${12 + curve} ${center + curve} ${12 + curve} ${center} ${12 + half} C${center - curve} ${12 + curve} ${center - curve} ${12 + curve} ${center - half} 12 C${center - curve} ${12 - curve} ${center - curve} ${12 - curve} ${center} ${12 - half}Z`}
          />
        );
      })}
    </svg>
  );
}
